import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import ArrowDown from "../../../assets/icons/icon-caret-down.svg";
import * as S from "./styles";
import { H5, TaglineP } from "../../../styles/stylesTypography";
import { theme } from "../../../styles/theme";
import FunctionBlockedOverlay from "../../functionBlocked";

const isFunctionBlocked = (user) => {
  const planId = user?.contrato?.agro_plan_config?.agro_plan_id;
  const diasRestante = Number(user?.contrato?.dias_restante ?? 0);
  return (
    planId === 7 ||
    planId === 6 ||
    planId === 4 ||
    (planId === 1 && diasRestante <= 0)
  );
};

const getUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("@userinfo"));
  } catch {
    return null;
  }
};

const DolarGraphic = ({ overlayMode = "self" }) => {
  const [data, setData] = useState([]);
  const [lastRate, setLastRate] = useState(null);
  const [lastDate, setLastDate] = useState("");
  const [percentageChange, setPercentageChange] = useState(null);
  const [period, setPeriod] = useState("daily");
  const [showSelect, setShowSelect] = useState(false);
  const blockedRef = useRef(null);
  const isBlocked = isFunctionBlocked(getUserFromStorage());

  const calculatePercentageChange = (data, period) => {
    let comparedValue;
    const todayRate = data[data.length - 1]?.rate;

    switch (period) {
      case "12m":
        const monthsAgoIndex = data.length - 365;
        comparedValue = monthsAgoIndex >= 0 ? data[monthsAgoIndex]?.rate : data[0]?.rate;
        break;
      case "6m":
        comparedValue = data[data.length - 180]?.rate || todayRate;
        break;
      case "1m":
        comparedValue = data[data.length - 30]?.rate || todayRate;
        break;
      default:
        comparedValue = data[data.length - 2]?.rate || todayRate;
    }

    const percentage = ((todayRate - comparedValue) / comparedValue) * 100;
    return { percentage, comparedValue };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint =
          period === "daily"
            ? "USD-BRL/2"
            : period === "12m"
              ? "USD-BRL/365"
              : period === "6m"
                ? "USD-BRL/180"
                : "USD-BRL/30";

        const response = await axios.get(
          `https://economia.awesomeapi.com.br/json/daily/${endpoint}`
        );
        const chartData = response.data.reverse().map((item) => ({
          date: new Date(item.timestamp * 1000).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }),
          rate: parseFloat(item.bid),
        }));

        setData(chartData);

        if (chartData.length > 1) {
          const last = chartData[chartData.length - 1];
          setLastRate(last.rate);
          setLastDate(last.date);

          const { percentage } = calculatePercentageChange(chartData, period);
          setPercentageChange(percentage);
        }
      } catch (error) {
        console.error("Erro ao buscar dados da API:", error);
      }
    };

    fetchData();
  }, [period]);

  const toggleSelect = () => setShowSelect(!showSelect);
  const selectOption = (value) => {
    setPeriod(value);
    setShowSelect(false);
  };

  const isNegative = percentageChange < 0;
  const lineColor = isNegative ? `${theme.colors.red.Vermelho_erro}` : `${theme.colors.green.Verde_AgroReceita}`;
  const gradientColor = isNegative ? "rgba(255, 82, 82, 0.4)" : "rgba(47, 187, 105, 0.4)";

  const chartConfig = {
    labels: data.map((item) => item.date),
    datasets: [
      {
        label: "USD/BRL",
        data: data.map((item) => item.rate),
        borderColor: lineColor,
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return "#00000000";
          }

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, gradientColor);
          gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

          return gradient;
        },
        tension: 0.4,
        fill: true,
        pointRadius: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const item = tooltipItems[0];
            return `Data: ${item.label}`;
          },
          label: (tooltipItem) => {
            return `Valor: R$ ${tooltipItem.raw.toFixed(2).replace(".", ",")}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
  };

  const optionsMap = {
    daily: "Diário",
    "1m": "1 Mês",
    "6m": "6 Meses",
    "12m": "12 Meses",
  };

  const percentageColor = isNegative ? `${theme.colors.red.Vermelho_erro}` : `${theme.colors.green.Verde_AgroReceita}`;

  // Nova função para retornar a descrição correta baseada no período
  const getPeriodDescription = () => {
    switch (period) {
      case "daily":
        return "Hoje";
      case "1m":
        return "Últimos 30 dias";
      case "6m":
        return "Últimos 6 meses";
      case "12m":
        return "Últimos 12 meses";
      default:
        return "";
    }
  };

  return (
    <S.Container ref={blockedRef}>
      {lastRate ? (
        <>
          <div className="rate-info">
            <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={600}>
              Atualizado hoje {lastDate}
            </TaglineP>

            <TaglineP color={theme.colors.blue.Azul_link} fontWeight={800}>BRL / USD</TaglineP>

            <H5 color={theme.colors.gray.Cinza_2}>R$ {lastRate.toFixed(2).replace(".", ",")}</H5>

            <div className="custom-selector" onClick={toggleSelect}>
              <div className="rate-details">
                {percentageChange !== null && (
                  <span
                    style={{
                      color: percentageColor,
                      fontSize: "11px",
                    }}
                  >
                    {percentageChange > 0 ? "+" : ""}
                    {percentageChange.toFixed(2)}% <br />{getPeriodDescription()}
                  </span>
                )}


                <button>
                  <img src={ArrowDown} alt="Abrir seletor" />
                </button>

                {showSelect && (
                  <ul>
                    {Object.entries(optionsMap).map(([value, label]) => (
                      <li
                        key={value}
                        onClick={() => selectOption(value)}
                        className={period === value ? "selected" : ""}
                      >
                        {label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
          {data.length > 0 && (
            <div className="chart-wrapper">
              <Line data={chartConfig} options={options} />
            </div>
          )}
        </>
      ) : (
        <></>
      )}

      {overlayMode !== "none" && isBlocked && (
        <FunctionBlockedOverlay
          visible
          mode="cover-parent"
          attachTo={blockedRef}
          onActivate={() => {}}
        />
      )}
    </S.Container>
  );
};

export default DolarGraphic;
